import { useMapContext } from "@evergis/react";
import { Circle } from "@evergis/sgis/es/controls/Circle";
import { DrawingFinishEvent } from "@evergis/sgis/es/controls/Control";
import polygonClipping, { Geom } from "polygon-clipping";
import { FC, useCallback, useEffect, useRef } from "react";

import { BaseCircleCreator } from "../../../../../react-sgis/controls/baseControls/BaseCircleCreator";
import { ShadowedCircleSymbol } from "../../../../../react-sgis/symbols/editorSymbols/ShadowedCircleSymbol";
import { ShadowedPolySymbol } from "../../../../../react-sgis/symbols/editorSymbols/ShadowedPolySymbol";

import { getRingsWithoutDuplicates } from "../../../../../utils/geometry";
import { createPolygonFeature } from "./utils/createPolygonFeature";
import { useSelectionEdit } from "./hooks/useSelectionEdit";
import { useGeometrySelection } from "../../../../../hooks/geometrySelection/useGeometrySelection";
import { useGeometrySelectionContext } from "../../../../../hooks/geometrySelection/useGeometrySelectionContext";
import { GeometrySelectionSymbolType, useSelectionSymbols } from "./hooks/useSelectionSymbols";

import { GeometrySelectionToolTypes, GeometrySelectionTypes } from "../types";
import { SelectionProps } from "./types";

export const CircleSelection: FC<SelectionProps> = ({ mode }) => {
  const { addFeature, replaceFeature } = useGeometrySelectionContext();
  const [{ history }] = useGeometrySelection();
  const { map, painter } = useMapContext();
  const createControl = useRef<BaseCircleCreator>();
  const symbols = useSelectionSymbols();

  const switchMode = useCallback((create: boolean) => {
    createControl.current.isActive = create;
  }, []);

  const createFeature = useCallback(
    (event: DrawingFinishEvent<Circle & { _rings?: [number, number][][] }>) => {
      const shouldRewrite =
        history.features.length > 0 &&
        history.step > -1 &&
        [GeometrySelectionToolTypes.Unite, GeometrySelectionToolTypes.Crop].includes(mode);

      let result;

      if (mode === GeometrySelectionToolTypes.Unite && history.features.length > 0 && history.step > -1) {
        result = polygonClipping
          .union([...history.features[history.step].map(item => item._rings), event.feature._rings] as Geom)
          // polygonClipping.union adds last point duplicate to polygons without intersection
          .map(getRingsWithoutDuplicates)
          .map(rings =>
            createPolygonFeature(
              rings,
              map.crs,
              symbols[GeometrySelectionSymbolType.polygonCreate] as ShadowedPolySymbol,
            ),
          );
      } else if (mode === GeometrySelectionToolTypes.Crop && history.features.length > 0 && history.step > -1) {
        result = polygonClipping
          .difference(
            [...history.features[history.step].map(item => item._rings)] as Geom,
            event.feature._rings as Geom,
          )
          .map(getRingsWithoutDuplicates)
          .map(rings =>
            createPolygonFeature(
              rings,
              map.crs,
              symbols[GeometrySelectionSymbolType.polygonCreate] as ShadowedPolySymbol,
            ),
          );
      } else {
        result = createPolygonFeature(
          [...event.feature._rings],
          map.crs,
          symbols[GeometrySelectionSymbolType.polygonCreate] as ShadowedPolySymbol,
        );
      }

      if (mode === GeometrySelectionToolTypes.Replace && history.step > -1) {
        replaceFeature(result);
      } else {
        addFeature(result, shouldRewrite);
      }
    },
    [history.features, history.step, mode, map.crs, symbols, replaceFeature, addFeature],
  );

  const offEvents = useCallback(() => {
    createControl.current.off("drawingFinish", createFeature);
  }, [createFeature]);

  const clearProperties = useCallback(() => {
    createControl.current = undefined;
  }, []);

  const initCreateMode = useCallback(() => {
    if (!createControl.current) {
      createControl.current = new BaseCircleCreator(map, painter, {
        isActive: false,
        useTempLayer: true,
        symbol: symbols[GeometrySelectionSymbolType.circleCreate].clone() as ShadowedCircleSymbol,
        createByClick: true,
        showRadiusLabel: true,
      });

      createControl.current.on("drawingFinish", createFeature);
    }
  }, [createFeature, map, painter]);

  useEffect(() => {
    initCreateMode();
    switchMode(true);

    return () => {
      switchMode(false);
      offEvents();
      clearProperties();
    };
  }, [initCreateMode]); // eslint-disable-line

  useSelectionEdit(GeometrySelectionTypes.Circle, map, painter, mode, switchMode);

  return null;
};
